import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import SuccessAlert from "../child/Alert/SuccessAlert";
import DengerAlert from "../child/Alert/DengerAlert";
import SearchableSelect from "../child/SearchableSelect";
import { handleUnauthorized } from "../../utils/apiHelper";
import { formatApiDate } from "../../utils/dateHelper";
import {
  fetchJoinGuestLists,
  fetchScannedGuestLists,
  fetchJoinGuestClubs,
  fetchJoinGuestHosts,
  fetchJoinGuestPromoters,
} from "../../services/joinGuestService";

const formatDate = (dateStr) => formatApiDate(dateStr);

const formatDateTime = (dateStr) =>
  dateStr ? moment(dateStr).format("DD-MM-YYYY HH:mm") : "N/A";

const getExpiryScanDateTime = (item) => {
  const scan = item?.userscanDateTime || item?.scanTime;
  if (scan) return moment(scan).add(24, "hours");
  if (item?.expiryScanTime) return moment(item.expiryScanTime);
  return null;
};

/** Normalize filter API payload into react-select options */
const toSelectOptions = (payload, labelKeys = []) => {
  const raw =
    payload?.data?.data ||
    payload?.data?.result ||
    payload?.data?.list ||
    payload?.data ||
    [];
  const list = Array.isArray(raw) ? raw : [];

  return list
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const value = item._id || item.id || item.value;
      if (!value) return null;

      const label =
        labelKeys.map((key) => item[key]).find(Boolean) ||
        item.fullName ||
        item.name ||
        item.label ||
        item.title ||
        String(value);

      return { value: String(value), label: String(label).trim() };
    })
    .filter(Boolean);
};

/**
 * Shared list for Joined / Scanned guest lists.
 * @param {string} status - API status filter (e.g. "joined,scanned" or "scanned")
 * @param {string} viewBasePath - route prefix for view (e.g. "/joinedGuest")
 * @param {string} hostId - locked host id (skips host dropdown)
 * @param {string} promoterId - locked promoter id (skips promoter dropdown)
 * @param {boolean} hideHostFilter
 * @param {boolean} hidePromoterFilter
 * @param {boolean} hideVenueFilter
 * @param {boolean} hideHostColumn
 * @param {boolean} hideVenueColumn
 * @param {boolean} hidePromoterColumn
 * @param {boolean} hideGuestTitleColumn
 * @param {boolean} hideCard - skip outer card wrapper (for embedding in another card)
 */
const JoinedguestListLayer = ({
  status = "joined,scanned",
  viewBasePath = "/joinedGuest",
  hostId = "",
  promoterId = "",
  hideHostFilter = false,
  hidePromoterFilter = false,
  hideVenueFilter = false,
  hideHostColumn = false,
  hideVenueColumn = false,
  hidePromoterColumn = false,
  hideGuestTitleColumn = false,
  hideCard = false,
  guestListId = "",
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [sortColumn, setSortColumn] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [promoterFilter, setPromoterFilter] = useState(null);
  const [hostFilter, setHostFilter] = useState(null);
  const [clubFilter, setClubFilter] = useState(null);
  const [promoterOptions, setPromoterOptions] = useState([]);
  const [hostOptions, setHostOptions] = useState([]);
  const [clubOptions, setClubOptions] = useState([]);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const navigate = useNavigate();

  const isScannedOnly = status === "scanned";
  const showHostFilter = !hideHostFilter && !hostId;
  const showPromoterFilter = !hidePromoterFilter && !promoterId;
  const showVenueFilter = !hideVenueFilter;
  const showHostColumn = !hideHostColumn;
  const showVenueColumn = !hideVenueColumn;
  const showPromoterColumn = !hidePromoterColumn;
  const showGuestTitleColumn = !hideGuestTitleColumn;
  const colSpan =
    3 +
    (showPromoterColumn ? 1 : 0) +
    (showHostColumn ? 1 : 0) +
    (showVenueColumn ? 1 : 0) +
    (showGuestTitleColumn ? 1 : 0) +
    (isScannedOnly ? 2 : 1);

  useEffect(() => {
    fetchFilterOptions();
  }, [showHostFilter, showPromoterFilter, showVenueFilter, isScannedOnly]);

  useEffect(() => {
    fetchData();
  }, [
    currentPage,
    searchQuery,
    itemsPerPage,
    sortOrder,
    sortColumn,
    status,
    startDate,
    endDate,
    promoterFilter,
    hostFilter,
    clubFilter,
    hostId,
    promoterId,
  ]);

  const fetchFilterOptions = async () => {
    try {
      setFiltersLoading(true);
      const requests = [];
      if (showVenueFilter) requests.push(fetchJoinGuestClubs());
      if (showHostFilter) requests.push(fetchJoinGuestHosts());
      if (showPromoterFilter) requests.push(fetchJoinGuestPromoters());

      if (requests.length === 0) {
        setFiltersLoading(false);
        return;
      }

      const results = await Promise.all(requests);
      let nextIndex = 0;
      const clubsRes = showVenueFilter ? results[nextIndex++] : null;
      const hostsRes = showHostFilter ? results[nextIndex++] : null;
      const promotersRes = showPromoterFilter ? results[nextIndex++] : null;
      if (showVenueFilter) {
        setClubOptions(
          toSelectOptions(clubsRes, ["clubName", "name", "fullName"])
        );
      }
      if (showHostFilter) {
        setHostOptions(
          toSelectOptions(hostsRes, ["hostfullName", "fullName", "name"])
        );
      }
      if (showPromoterFilter) {
        setPromoterOptions(
          toSelectOptions(promotersRes, [
            "promoterFullName",
            "fullName",
            "name",
            "promoterTitle",
          ])
        );
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        handleUnauthorized(navigate);
        return;
      }
      setErrorMessage(
        error?.response?.data?.message || "Failed to fetch filter options"
      );
      setTimeout(() => setErrorMessage(""), 3000);
    } finally {
      setFiltersLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);

      const formattedStartDate = startDate
        ? moment(startDate).format("YYYY-MM-DD")
        : "";
      const formattedEndDate = endDate
        ? moment(endDate).format("YYYY-MM-DD")
        : "";

      if (
        formattedStartDate &&
        formattedEndDate &&
        moment(formattedStartDate).isAfter(moment(formattedEndDate))
      ) {
        setErrorMessage("Start date cannot be greater than end date");
        setTimeout(() => setErrorMessage(""), 3000);
        setLoading(false);
        return;
      }

      const listParams = {
        search: searchQuery || "",
        promoterId: promoterId || promoterFilter?.value || "",
        hostId: hostId || hostFilter?.value || "",
        clubId: clubFilter?.value || "",
        userId: "",
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        sortBy:
          isScannedOnly &&
          (sortColumn === "createdAt" || sortColumn === "DateOfjoining")
            ? "userscanDateTime"
            : sortColumn,
        sortOrder,
        page: currentPage,
        limit: itemsPerPage,
      };

      if (isScannedOnly) {
        listParams.dateBy = "userscanDateTime";
      }

      const response = isScannedOnly
        ? await fetchScannedGuestLists(listParams)
        : await fetchJoinGuestLists({
            ...listParams,
            status,
          });

      const data = response?.data || {};
      setItems(data.data || []);
      setTotalItems(data.pagination?.total || data.total || 0);
      setTotalPages(data.pagination?.totalPages || data.totalPages || 1);
    } catch (error) {
      if (error?.response?.status === 401) {
        handleUnauthorized(navigate);
        return;
      }
      setItems([]);
      setTotalItems(0);
      setTotalPages(1);
      setErrorMessage(
        error?.response?.data?.message || "Failed to fetch guest list"
      );
      setTimeout(() => setErrorMessage(""), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (setter) => (option) => {
    setter(option);
    setCurrentPage(1);
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortOrder("desc");
    }
    setCurrentPage(1);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value, 10));
    setCurrentPage(1);
  };

  const getPaginationRange = () => {
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + 4);

    if (end - start < 4) {
      start = Math.max(1, end - 4);
    }

    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const renderSortIcons = (column) => (
    <span className="sort-colume">
      <Icon
        icon="mdi:arrow-up"
        className={`arrow ${sortColumn === column && sortOrder === "asc" ? "active" : ""
          }`}
      />
      <Icon
        icon="mdi:arrow-down"
        className={`arrow ${sortColumn === column && sortOrder === "desc" ? "active" : ""
          }`}
      />
    </span>
  );

  return (
    <div className={hideCard ? "" : "card h-100 p-0 radius-12"}>
      {successMessage && (
        <SuccessAlert
          message={successMessage}
          onClose={() => setSuccessMessage("")}
        />
      )}
      {errorMessage && (
        <DengerAlert
          message={errorMessage}
          onClose={() => setErrorMessage("")}
        />
      )}
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
        <div className="d-flex align-items-center flex-wrap gap-3">
          <span className="text-md fw-medium text-secondary-light mb-0">
            Show
          </span>
          <select
            className="form-select form-select-sm w-auto ps-12 py-6"
            style={{
              fontSize: "16px",
              borderRadius: "7px",
              backgroundPositionX: "unset",
              backgroundPosition: "right 0.75rem center",
            }}
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
          >
            <option value="10">10 items per page</option>
            <option value="20">20 items per page</option>
            <option value="50">50 items per page</option>
            <option value="100">100 items per page</option>
          </select>
          <form className="navbar-search" style={{ position: "relative" }}>
            <input
              type="text"
              className="bg-base h-40-px w-auto pe-40"
              name="search"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                paddingRight: searchQuery ? "5rem" : "2.5rem",
              }}
            />
            <Icon
              icon="ion:search-outline"
              className="icon"
              style={{
                position: "absolute",
                right: searchQuery ? "3.25rem" : "1rem",
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: "1.25rem",
                color: "#999",
                pointerEvents: "none",
              }}
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery("");
                  setCurrentPage(1);
                }}
                style={{
                  position: "absolute",
                  right: "1rem",
                  top: "30%",
                  transform: "translateY(-50%)",
                  fontSize: "1.25rem",
                  color: "#000",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <Icon icon="ic:round-close" />
              </button>
            )}
          </form>
          {showPromoterFilter && (
            <SearchableSelect
              options={promoterOptions}
              value={promoterFilter}
              onChange={handleFilterChange(setPromoterFilter)}
              placeholder="All Promoters"
              isLoading={filtersLoading}
            />
          )}
          {showHostFilter && (
            <SearchableSelect
              options={hostOptions}
              value={hostFilter}
              onChange={handleFilterChange(setHostFilter)}
              placeholder="All Hosts"
              isLoading={filtersLoading}
            />
          )}
          {showVenueFilter && (
            <SearchableSelect
              options={clubOptions}
              value={clubFilter}
              onChange={handleFilterChange(setClubFilter)}
              placeholder="All Venues"
              isLoading={filtersLoading}
            />
          )}
          <div style={{ position: "relative", maxWidth: "200px" }}>
            <DatePicker
              selected={startDate}
              onChange={(date) => {
                setStartDate(date);
                setEndDate(null);
                setCurrentPage(1);
              }}
              preventOpenOnFocus={false}
              onKeyDown={(e) => e.preventDefault()}
              className="form-control no-caret"
              placeholderText="Start Date"
              dateFormat="yyyy-MM-dd"
              isClearable
            />
          </div>
          <div style={{ position: "relative", maxWidth: "200px" }}>
            <DatePicker
              selected={endDate}
              onChange={(date) => {
                setEndDate(date);
                setCurrentPage(1);
              }}
              minDate={startDate}
              preventOpenOnFocus={false}
              onKeyDown={(e) => e.preventDefault()}
              className="form-control no-caret"
              placeholderText="End Date"
              dateFormat="yyyy-MM-dd"
              isClearable
            />
          </div>
        </div>
      </div>
      <div className="card-body p-24">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr>
                <th
                  onClick={() => handleSort("userFullName")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-0">
                    User Name
                    {renderSortIcons("userFullName")}
                  </div>
                </th>
                {showPromoterColumn && (
                  <th
                    onClick={() => handleSort("promoterFullName")}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="d-flex align-items-center gap-0">
                      Promoter
                      {renderSortIcons("promoterFullName")}
                    </div>
                  </th>
                )}
                {showHostColumn && (
                  <th
                    onClick={() => handleSort("hostfullName")}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="d-flex align-items-center gap-0">
                      Host
                      {renderSortIcons("hostfullName")}
                    </div>
                  </th>
                )}
                {showVenueColumn && (
                  <th
                    onClick={() => handleSort("clubName")}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="d-flex align-items-center gap-0">
                      Venue Name
                      {renderSortIcons("clubName")}
                    </div>
                  </th>
                )}
                {showGuestTitleColumn && (
                  <th
                    onClick={() => handleSort("promoterTitle")}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="d-flex align-items-center gap-0">
                      Guest Title
                      {renderSortIcons("promoterTitle")}
                    </div>
                  </th>
                )}

                <th
                  onClick={() => handleSort("status")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-0">
                    Status
                    {renderSortIcons("status")}
                  </div>
                </th>

                {isScannedOnly ? (
                  <>
                    <th
                      onClick={() => handleSort("userscanDateTime")}
                      style={{ cursor: "pointer" }}
                    >
                      <div className="d-flex align-items-center gap-0">
                        Scan Date
                        {renderSortIcons("userscanDateTime")}
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort("expiryScanTime")}
                      style={{ cursor: "pointer" }}
                    >
                      <div className="d-flex align-items-center gap-0">
                        Expiry Scan Date
                        {renderSortIcons("expiryScanTime")}
                      </div>
                    </th>
                  </>
                ) : (
                  <th
                    onClick={() => handleSort("DateOfjoining")}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="d-flex align-items-center gap-0">
                      Joined Date
                      {renderSortIcons("DateOfjoining")}
                    </div>
                  </th>
                )}
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={colSpan} className="text-center">
                    <span className="loader-15"></span>
                  </td>
                </tr>
              ) : items.length > 0 ? (
                items.map((item, index) => (
                  <tr
                    key={`${item._id}-${item.scanTime || item.userscanDateTime || index}`}
                  >
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {item.userFullName || "N/A"}
                      </span>
                    </td>
                    {showPromoterColumn && (
                      <td>
                        <span className="text-md mb-0 fw-normal text-secondary-light">
                          {item.promoterFullName || "N/A"}
                        </span>
                      </td>
                    )}
                    {showHostColumn && (
                      <td>
                        <span className="text-md mb-0 fw-normal text-secondary-light">
                          {item.hostfullName || "N/A"}
                        </span>
                      </td>
                    )}
                    {showVenueColumn && (
                      <td>
                        <span className="text-md mb-0 fw-normal text-secondary-light">
                          {item.clubName || "N/A"}
                        </span>
                      </td>
                    )}
                    {showGuestTitleColumn && (
                      <td>
                        <span className="text-md mb-0 fw-normal text-secondary-light">
                          {item.promoterTitle || "N/A"}
                        </span>
                      </td>
                    )}
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light text-capitalize">
                        {item.status || "N/A"}
                      </span>
                    </td>
                    {isScannedOnly ? (
                      <>
                        <td>
                          <span className="text-md mb-0 fw-normal text-secondary-light">
                            {formatDateTime(
                              item.userscanDateTime || item.scanTime
                            )}
                          </span>
                        </td>
                        <td>
                          <span className="text-md mb-0 fw-normal text-secondary-light">
                            {formatDateTime(getExpiryScanDateTime(item))}
                          </span>
                        </td>
                      </>
                    ) : (
                      <td>
                        <span className="text-md mb-0 fw-normal text-secondary-light text-capitalize">
                          {formatDate(item.DateOfjoining || item.createdAt)}
                        </span>
                      </td>
                    )}
                    <td>
                      <div className="d-flex align-items-center gap-10">
                        <button
                          type="button"
                          onClick={() =>
                            navigate(`${viewBasePath}/view-guest/${item._id}`, {
                              state: {
                                guest: item,
                                guestListId: guestListId || undefined,
                              },
                            })
                          }
                          title="View"
                          className="bg-info-focus bg-hover-info-200 text-info-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                        >
                          <Icon
                            icon="majesticons:eye-line"
                            className="icon text-xl"
                          />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={colSpan} className="text-center">
                    No Data Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
          <span>
            {totalItems === 0 ? (
              <>No entry to show</>
            ) : (
              <>
                Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                {Math.min(currentPage * itemsPerPage, totalItems)} of{" "}
                {totalItems} entries
              </>
            )}
          </span>
          <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                type="button"
                className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                onClick={handlePrev}
              >
                <Icon icon="ep:d-arrow-left" />
              </button>
            </li>
            {getPaginationRange().map((page) => (
              <li key={page} className="page-item">
                <button
                  type="button"
                  className={`page-link text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${currentPage === page
                    ? "bg-primary-600 text-white"
                    : "bg-neutral-200"
                    }`}
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </button>
              </li>
            ))}
            <li
              className={`page-item ${currentPage === totalPages ? "disabled" : ""
                }`}
            >
              <button
                type="button"
                className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                onClick={handleNext}
              >
                <Icon icon="ep:d-arrow-right" />
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default JoinedguestListLayer;
